// src/stores/userStore.js
import { defineStore } from 'pinia';
import { fetchCurrentUser } from '../api/modules/userApi';
import { clearAllAdaptiveRtDrafts } from '@/utils/adaptiveRtDraftStorage';
import {
  ACCESS_KEY,
  AUTH_TIMEOUT_MS,
  REFRESH_KEY,
  USER_KEY,
  isAccessExpired,
  isTransientAuthError,
  refreshSessionTokens,
} from '../api/authSession';
import { ROLE_ADMIN, ROLE_DEVELOPER, ROLE_RESEARCHER, ROLE_USER } from '@/constants/access';
import { AUTH_SESSION_CHECK_FAILED, AUTH_SESSION_EXPIRED } from '@/constants/userMessages';

/** @type {Promise<void> | null} */
let restoreSessionPromise = null;
let storageSyncBound = false;

/**
 * Other tabs share localStorage. When one tab logs out or rotates tokens,
 * this tab drops the copy it would otherwise send to the blacklist.
 * @param {ReturnType<typeof useUserStore>} store
 */
function bindAuthStorageSync(store) {
  if (storageSyncBound || typeof window === 'undefined') return;
  storageSyncBound = true;
  window.addEventListener('storage', (event) => {
    if (event.key !== ACCESS_KEY && event.key !== REFRESH_KEY && event.key !== USER_KEY) return;
    const access = localStorage.getItem(ACCESS_KEY);
    const refresh = localStorage.getItem(REFRESH_KEY);
    if (!access && !refresh) {
      if (store.user || store.accessToken || store.refreshToken) {
        store.clearUser();
      }
      return;
    }
    store.accessToken = access;
    store.refreshToken = refresh;
  });
}

export const useUserStore = defineStore('userStore', {
  state: () => ({
    /** Не подставляем user из localStorage: ждём restoreSession, иначе «ложный» вход. */
    user: null,
    /** После старта приложения и любой попытки восстановления сессии — true. */
    sessionReady: false,
    /** Почему вход нужно повторить: сеть или истёкшая сессия. Не стирается при переходе на главную. */
    sessionNotice: null,
    accessToken: localStorage.getItem(ACCESS_KEY) || null,
    refreshToken: localStorage.getItem(REFRESH_KEY) || null,
  }),
  getters: {
    role: (state) => Number(state.user?.role ?? 0),
    permissions: (state) => (Array.isArray(state.user?.permissions) ? state.user.permissions : []),
    quota: (state) => state.user?.quota || null,
    isApproved: (state) => {
      const role = Number(state.user?.role ?? 0);
      return role === ROLE_ADMIN || role === ROLE_RESEARCHER || role === ROLE_USER || role === ROLE_DEVELOPER;
    },
    isAdmin: (state) => {
      const role = Number(state.user?.role ?? 0);
      return role === ROLE_ADMIN || role === ROLE_DEVELOPER;
    },
    isDeveloper: (state) => Number(state.user?.role ?? 0) === ROLE_DEVELOPER,
    hasPermission: (state) => (permissionCode) => {
      const code = String(permissionCode || '').trim();
      if (!code) return false;
      const permissions = Array.isArray(state.user?.permissions) ? state.user.permissions : [];
      return permissions.includes(code);
    },
  },
  actions: {
    setUser(user) {
      this.user = user;
      this.sessionReady = true;
      if (user) {
        this.sessionNotice = null;
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      } else {
        localStorage.removeItem(USER_KEY);
      }
    },
    setTokens(access, refresh) {
      this.accessToken = access;
      this.refreshToken = refresh;
      if (access) localStorage.setItem(ACCESS_KEY, access);
      else localStorage.removeItem(ACCESS_KEY);
      if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
      else localStorage.removeItem(REFRESH_KEY);
    },
    clearUser() {
      this.user = null;
      this.accessToken = null;
      this.refreshToken = null;
      this.sessionNotice = null;
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
      clearAllAdaptiveRtDrafts();
      this.sessionReady = true;
    },
    /** Сессия точно недействительна: токены убираем и объясняем, почему форма входа снова на экране. */
    expireSession() {
      this.clearUser();
      this.sessionNotice = AUTH_SESSION_EXPIRED;
    },
    /** Сервер или сеть не ответили. Токены оставляем, форму входа открываем. */
    markSessionUnverified() {
      this.user = null;
      this.accessToken = localStorage.getItem(ACCESS_KEY);
      this.refreshToken = localStorage.getItem(REFRESH_KEY);
      this.sessionNotice = AUTH_SESSION_CHECK_FAILED;
      this.sessionReady = true;
    },
  /**
   * Восстановление JWT и профиля (single-flight: параллельные вызовы ждут один promise).
   * Вызывать из router.beforeEach; не дублировать из App.vue.
   */
    async refreshUser() {
      try {
        const user = await fetchCurrentUser();
        this.setUser(user);
        return user;
      } catch {
        return this.user;
      }
    },
    restoreSession() {
      bindAuthStorageSync(this);
      if (restoreSessionPromise) {
        return restoreSessionPromise;
      }
      if (this.sessionReady && this.user) {
        return Promise.resolve();
      }
      if (this.sessionReady && !this.sessionNotice) {
        return Promise.resolve();
      }

      restoreSessionPromise = this._restoreSessionOnce().finally(() => {
        restoreSessionPromise = null;
      });
      return restoreSessionPromise;
    },

    async _restoreSessionOnce() {
      const preservedNotice = this.sessionNotice;
      this.sessionNotice = null;
      let access = localStorage.getItem(ACCESS_KEY);
      const refresh = localStorage.getItem(REFRESH_KEY);

      if (!access && !refresh) {
        this.clearUser();
        if (preservedNotice) this.sessionNotice = preservedNotice;
        return;
      }

      if (isAccessExpired(access) || !access) {
        let result;
        try {
          result = await refreshSessionTokens(access);
        } catch {
          this.markSessionUnverified();
          return;
        }
        if (result.status === 'transient') {
          this.markSessionUnverified();
          return;
        }
        if (result.status !== 'ok' || !result.access) {
          this.expireSession();
          return;
        }
        this.setTokens(result.access, result.refresh);
        access = result.access;
      }

      if (!access) {
        this.expireSession();
        return;
      }

      try {
        const user = await fetchCurrentUser({ timeout: AUTH_TIMEOUT_MS });
        this.setUser(user);
      } catch (error) {
        if (isTransientAuthError(error)) {
          this.markSessionUnverified();
          return;
        }
        if (error?.response?.status === 401 && localStorage.getItem(REFRESH_KEY)) {
          let result;
          try {
            result = await refreshSessionTokens(access);
          } catch {
            this.markSessionUnverified();
            return;
          }
          if (result.status === 'transient') {
            this.markSessionUnverified();
            return;
          }
          if (result.status === 'ok' && result.access) {
            this.setTokens(result.access, result.refresh);
            try {
              const user = await fetchCurrentUser({ timeout: AUTH_TIMEOUT_MS });
              this.setUser(user);
              return;
            } catch (retryError) {
              if (isTransientAuthError(retryError)) {
                this.markSessionUnverified();
                return;
              }
            }
          }
        }
        this.expireSession();
      } finally {
        this.sessionReady = true;
      }
    },
  },
});
